import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useMenuStore } from '@/store/menuStore'
import {
  ACTION_GRANTS,
  getPageButtonVisibility,
  getPageButtonPermissions,
} from '@/config/buttonPermissionConfig'

interface MenuPermissionItem {
  name?: string
  Name?: string
  path?: string
  component?: string
  Component?: string
  componentPath?: string
  ComponentPath?: string
  IsButton?: boolean
  IsHide?: boolean
  meta?: { title?: string }
  children?: MenuPermissionItem[]
  Children?: MenuPermissionItem[]
}

const normalizeSlash = (value: string) => value.replace(/\\/g, '/').replace(/\/+/g, '/')

const normalizeRoutePath = (value: string) => {
  const path = normalizeSlash(value || '').trim()
  if (!path) return ''
  return `/${path.replace(/^\/+|\/+$/g, '')}`
}

const normalizeComponentPath = (value: string) => {
  let path = normalizeSlash(value || '').trim()
  path = path
    .replace(/^\.\//, '')
    .replace(/^@\/views\//, '')
    .replace(/^\/?src\/views\//, '')
    .replace(/^\/?views\//, '')
    .replace(/^\/+/, '')

  if (!path || !/\.vue$/i.test(path)) return ''
  return path.toLowerCase()
}

const getChildren = (menu: MenuPermissionItem) => {
  const children = Array.isArray(menu.children) ? menu.children : menu.Children
  return Array.isArray(children) ? children : []
}

const getMenuName = (menu: MenuPermissionItem) => {
  return String(menu.meta?.title || menu.name || menu.Name || '').trim()
}

const getMenuComponent = (menu: MenuPermissionItem) => {
  return String(
    menu.component ||
    menu.Component ||
    menu.componentPath ||
    menu.ComponentPath ||
    menu.path ||
    ''
  )
}

const findPageMenu = (
  menus: MenuPermissionItem[],
  componentPath: string,
  routePath: string,
  parentDisplayPath = ''
): MenuPermissionItem | null => {
  const targetComponent = normalizeComponentPath(componentPath)
  const targetRoute = normalizeRoutePath(routePath)

  for (const menu of menus) {
    if (!menu || menu.IsButton) continue

    const displayPath = normalizeRoutePath(`${parentDisplayPath}/${getMenuName(menu)}`)
    const configuredPath = String(menu.path || '')
    const componentMatches = targetComponent
      && normalizeComponentPath(getMenuComponent(menu)) === targetComponent
    const routeMatches = targetRoute
      && (normalizeRoutePath(configuredPath) === targetRoute || displayPath === targetRoute)

    if (componentMatches || routeMatches) return menu

    const found = findPageMenu(
      getChildren(menu).filter(child => !child.IsButton),
      componentPath,
      routePath,
      displayPath
    )
    if (found) return found
  }

  return null
}

const normalizePermissionName = (value: string) => value.trim().replace(/刪/g, '删')

// 汇总配置开发期一致性校验：记录已提示过的页面/权限，避免重复刷屏
const warnedKeys = new Set<string>()
const warnUnregistered = (componentPath: string, action: string) => {
  const key = `${componentPath}|${action}`
  if (warnedKeys.has(key)) return
  warnedKeys.add(key)
  console.warn(
    `[buttonPermission] 页面 "${componentPath}" 使用了按钮权限 "${action}"，` +
    `但未登记在 src/config/buttonPermissionConfig.ts 中，请补充配置。`
  )
}

export const useButtonPermission = (componentPath: string) => {
  const route = useRoute()
  const menuStore = useMenuStore()

  const pageMenu = computed(() => findPageMenu(
    menuStore.rawMenus as MenuPermissionItem[],
    componentPath,
    route.path
  ))

  const permissions = computed(() => {
    const permissionSet = new Set(
      getChildren(pageMenu.value || {})
        .filter(item => item.IsButton && !item.IsHide)
        .map(item => normalizePermissionName(getMenuName(item)))
        .filter(Boolean)
    )

    // 新增/编辑联动：页面配了编辑(或更新)权限则视为同时拥有新增权限，反之亦然
    // 判断时同样走配置文件映射，后端字段名改了也能正确联动
    const hasEdit = (ACTION_GRANTS['编辑'] || ['编辑']).some(name => permissionSet.has(name))
    const hasAdd = (ACTION_GRANTS['新增'] || ['新增']).some(name => permissionSet.has(name))
    if (hasEdit) permissionSet.add('新增')
    if (hasAdd) permissionSet.add('编辑')

    return permissionSet
  })

  const can = (action: string) => {
    const normalizedAction = normalizePermissionName(action)

    // 优先读取汇总配置文件中的显隐开关
    //   false -> 强制隐藏，无论后端权限如何配置，该按钮都不显示
    //   true / 未配置 -> 继续按后端菜单权限判断
    const visibility = getPageButtonVisibility(componentPath, normalizedAction)
    if (visibility === false) return false

    const acceptedNames = ACTION_GRANTS[normalizedAction] || [normalizedAction]
    const result = acceptedNames.some(name => permissions.value.has(normalizePermissionName(name)))

    // 开发期校验：确保页面使用的权限点已登记在汇总配置中
    if (process.env.NODE_ENV === 'development') {
      const registered = getPageButtonPermissions(componentPath)
      if (!registered.includes(normalizedAction)) {
        warnUnregistered(componentPath, normalizedAction)
      }
    }
    return result
  }

  const canAny = (...actions: string[]) => actions.some(can)

  return {
    can,
    canAny,
    permissions,
    pageMenu,
  }
}

